<template>
  <div class="emailing-view flex h-[calc(100vh-80px)]">
    <!-- Détecteur de changement de marque -->
    <BrandChangeDetector :support-id="route.params.id" />

    <!-- Overlay de chargement -->
    <div v-if="isSaving" class="pdf-loading-overlay">
      <div class="pdf-loading-content loader-standard">
        <div class="spinner" />
        <div class="loading-text">
          Enregistrement en cours…
        </div>
      </div>
    </div>

    <!-- Panneau latéral -->
    <ProductsPanel />

    <!-- Zone principale -->
    <div class="flex-1 bg-gray-100 p-8 overflow-auto">
      <div class="max-w-5xl mx-auto">
        <!-- En-tête -->
        <div class="mb-8 flex items-center justify-between">
          <h1 class="text-2xl font-bold text-gray-900">
            {{
              isNewSupport
                ? "Création d'un support réseau social"
                : "Édition d'un support réseau social"
            }}
          </h1>
          <div class="flex items-center gap-4">
            <button
              class="px-4 py-2 bg-brand text-white rounded-md shadow-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              :disabled="isSaving"
              @click="handleSaveAndGeneratePDF"
            >
              <i v-if="isSaving" class="fas fa-spinner fa-spin" />
              <span>{{ isSaving ? "Enregistrement..." : "Enregistrer" }}</span>
            </button>
          </div>
        </div>

        <!-- Menu de sélection -->
        <div class="mb-8">
          <button
            class="w-full flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:border-brand transition-colors"
            @click="isMenuOpen = !isMenuOpen"
          >
            <div class="flex items-center gap-4">
              <div
                v-if="selectedNetwork"
                class="text-gray-700"
                v-html="selectedNetworkData?.icon"
              />
              <span class="font-medium">
                {{
                  selectedNetwork
                    ? selectedNetworkData?.name
                    : "Sélectionner un réseau social"
                }}
                {{
                  selectedFormat ? ` - ${getFormatName(selectedFormat)}` : ""
                }}
              </span>
            </div>
            <svg
              class="w-5 h-5 text-gray-500 transition-transform"
              :class="{ 'rotate-180': isMenuOpen }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          <!-- Menu déroulant -->
          <div
            v-if="isMenuOpen"
            class="mt-2 bg-white rounded-lg border border-gray-200 shadow-lg overflow-hidden"
          >
            <!-- Sélection du réseau social -->
            <div class="p-4 border-b border-gray-200">
              <h2 class="text-lg font-semibold mb-4">
                Choisissez votre réseau social
              </h2>
              <div class="grid grid-cols-4 gap-4">
                <button
                  v-for="network in networks"
                  :key="network.id"
                  class="p-4 border rounded-lg flex flex-col items-center justify-center gap-2"
                  :class="
                    selectedNetwork === network.id
                      ? 'border-brand bg-red-50'
                      : 'border-gray-200'
                  "
                  @click="selectNetwork(network.id)"
                >
                  <div class="text-gray-700" v-html="network.icon" />
                  <span class="font-medium">{{ network.name }}</span>
                </button>
              </div>
            </div>

            <!-- Sélection du format pour Instagram -->
            <div
              v-if="selectedNetwork === 'instagram'"
              class="p-4 border-b border-gray-200"
            >
              <h2 class="text-lg font-semibold mb-4">
                Choisissez le format
              </h2>
              <div class="grid grid-cols-3 gap-4">
                <button
                  v-for="format in instagramFormats"
                  :key="format.id"
                  class="p-4 border rounded-lg flex flex-col items-center justify-center gap-2"
                  :class="
                    selectedFormat === format.id
                      ? 'border-brand bg-red-50'
                      : 'border-gray-200'
                  "
                  @click="selectFormat(format.id)"
                >
                  <div
                    class="w-32 aspect-square bg-gray-100 rounded flex items-center justify-center mx-auto"
                  >
                    <div
                      class="bg-gray-300"
                      :style="{
                        width:
                          format.id === 'square'
                            ? '100%'
                            : format.id === 'landscape'
                              ? '100%'
                              : '80%',
                        height:
                          format.id === 'square'
                            ? '100%'
                            : format.id === 'landscape'
                              ? '52.4%'
                              : '125%',
                        transform:
                          format.id === 'portrait' ? 'scale(0.8)' : 'none',
                      }"
                    />
                  </div>
                  <span class="font-medium">{{ format.name }}</span>
                  <span class="text-sm text-gray-500">{{
                    format.dimensions
                  }}</span>
                </button>
              </div>
            </div>

            <!-- Sélection du format pour Facebook -->
            <div
              v-if="selectedNetwork === 'facebook'"
              class="p-4 border-b border-gray-200"
            >
              <h2 class="text-lg font-semibold mb-4">
                Choisissez le format
              </h2>
              <div class="grid grid-cols-2 gap-4">
                <button
                  v-for="format in facebookFormats"
                  :key="format.id"
                  class="p-4 border rounded-lg flex flex-col items-center justify-center gap-2"
                  :class="
                    selectedFormat === format.id
                      ? 'border-brand bg-red-50'
                      : 'border-gray-200'
                  "
                  @click="selectFormat(format.id)"
                >
                  <div
                    class="w-32 aspect-square bg-gray-100 rounded flex items-center justify-center mx-auto"
                  >
                    <div
                      class="bg-gray-300"
                      :style="{
                        width: format.id === 'square' ? '100%' : '100%',
                        height: format.id === 'square' ? '100%' : '52.5%',
                      }"
                    />
                  </div>
                  <span class="font-medium">{{ format.name }}</span>
                  <span class="text-sm text-gray-500">{{
                    format.dimensions
                  }}</span>
                </button>
              </div>
            </div>

            <!-- Sélection du format pour X (Twitter) -->
            <div
              v-if="selectedNetwork === 'twitter'"
              class="p-4 border-b border-gray-200"
            >
              <h2 class="text-lg font-semibold mb-4">
                Choisissez le format
              </h2>
              <div class="grid grid-cols-2 gap-4">
                <button
                  v-for="format in twitterFormats"
                  :key="format.id"
                  class="p-4 border rounded-lg flex flex-col items-center justify-center gap-2"
                  :class="
                    selectedFormat === format.id
                      ? 'border-brand bg-red-50'
                      : 'border-gray-200'
                  "
                  @click="selectFormat(format.id)"
                >
                  <div
                    class="w-32 aspect-square bg-gray-100 rounded flex items-center justify-center mx-auto"
                  >
                    <div
                      class="bg-gray-300"
                      :style="{
                        width: format.id === 'square' ? '100%' : '100%',
                        height: format.id === 'square' ? '100%' : '58.1%',
                      }"
                    />
                  </div>
                  <span class="font-medium">{{ format.name }}</span>
                  <span class="text-sm text-gray-500">{{
                    format.dimensions
                  }}</span>
                </button>
              </div>
            </div>

            <!-- Sélection du format pour LinkedIn -->
            <div
              v-if="selectedNetwork === 'linkedin'"
              class="p-4 border-b border-gray-200"
            >
              <h2 class="text-lg font-semibold mb-4">
                Choisissez le format
              </h2>
              <div class="grid grid-cols-2 gap-4">
                <button
                  v-for="format in linkedinFormats"
                  :key="format.id"
                  class="p-4 border rounded-lg flex flex-col items-center justify-center gap-2"
                  :class="
                    selectedFormat === format.id
                      ? 'border-brand bg-red-50'
                      : 'border-gray-200'
                  "
                  @click="selectFormat(format.id)"
                >
                  <div
                    class="w-32 aspect-square bg-gray-100 rounded flex items-center justify-center mx-auto"
                  >
                    <div
                      class="bg-gray-300"
                      :style="{
                        width: format.id === 'square' ? '100%' : '100%',
                        height: format.id === 'square' ? '100%' : '50%',
                      }"
                    />
                  </div>
                  <span class="font-medium">{{ format.name }}</span>
                  <span class="text-sm text-gray-500">{{
                    format.dimensions
                  }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        <!-- Canvas -->
        <component
          :is="getCanvasComponent"
          v-if="selectedNetwork && selectedFormat"
          ref="supportCanvasRef"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ProductsPanel from "@/components/supports/panels/ProductsPanel.vue";
import { useSupportStore } from "@/stores/supports/useSupportStore";
import { computed, onMounted, ref, watch } from "@vue/runtime-core";
import { useRoute, useRouter } from "vue-router";
import BrandChangeDetector from "@/components/supports/BrandChangeDetector.vue";
import { useToast } from "vue-toastification";

// Import des composants de canvas pour les réseaux sociaux
import FacebookLandscapeCanva from "@/components/supports/support_reseaux/FacebookLandscapeCanva.vue";
import FacebookSquareCanva from "@/components/supports/support_reseaux/FacebookSquareCanva.vue";
import InstagramLandscapeCanva from "@/components/supports/support_reseaux/InstagramLandscapeCanva.vue";
import InstagramPortraitCanva from "@/components/supports/support_reseaux/InstagramPortraitCanva.vue";
import InstagramSquareCanva from "@/components/supports/support_reseaux/InstagramSquareCanva.vue";
import LinkedInLandscapeCanva from "@/components/supports/support_reseaux/LinkedInLandscapeCanva.vue";
import LinkedInSquareCanva from "@/components/supports/support_reseaux/LinkedInSquareCanva.vue";
import TwitterLandscapeCanva from "@/components/supports/support_reseaux/TwitterLandscapeCanva.vue";
import TwitterSquareCanva from "@/components/supports/support_reseaux/TwitterSquareCanva.vue";

interface Network {
  id: string;
  name: string;
  icon: string;
}

const router = useRouter();
const toast = useToast();
const successSound = new Audio("/sounds/success.wav");
const errorSound = new Audio("/sounds/error.mp3");

const playSound = (sound: HTMLAudioElement) => {
  sound.currentTime = 0; // Réinitialiser le son
  sound.play().catch((error) => {
    console.warn("Erreur lors de la lecture du son:", error);
  });

  // Couper le son après 200ms pour le son iPhone
  setTimeout(() => {
    sound.pause();
    sound.currentTime = 0;
  }, 400);
};
// Ajuster le volume des sons
successSound.volume = 0.3; // 30% du volume pour le son iPhone
errorSound.volume = 0.15; // 15% du volume pour l'erreur

const supportStore = useSupportStore();
const selectedNetwork = ref("");
const selectedFormat = ref("");
const isMenuOpen = ref(false);
const isSaving = ref(false);
const supportCanvasRef = ref();

const route = useRoute();
const isNewSupport = computed(
  () => !route.params.id || route.params.id === "nouveau"
);

const networks: Network[] = [
  {
    id: "instagram",
    name: "Instagram",
    icon: `<svg class="w-8 h-8" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2c2.717 0 3.056.01 4.122.06 1.065.05 1.79.217 2.428.465.66.254 1.216.598 1.772 1.153a4.908 4.908 0 0 1 1.153 1.772c.247.637.415 1.363.465 2.428.047 1.066.06 1.405.06 4.122 0 2.717-.01 3.056-.06 4.122-.05 1.065-.218 1.79-.465 2.428a4.883 4.883 0 0 1-1.153 1.772 4.915 4.915 0 0 1-1.772 1.153c-.637.247-1.363.415-2.428.465-1.066.047-1.405.06-4.122.06-2.717 0-3.056-.01-4.122-.06-1.065-.05-1.79-.218-2.428-.465a4.89 4.89 0 0 1-1.772-1.153 4.904 4.904 0 0 1-1.153-1.772c-.248-.637-.415-1.363-.465-2.428C2.013 15.056 2 14.717 2 12c0-2.717.01-3.056.06-4.122.05-1.066.217-1.79.465-2.428a4.88 4.88 0 0 1 1.153-1.772A4.897 4.897 0 0 1 5.45 2.525c.638-.248 1.362-.415 2.428-.465C8.944 2.013 9.283 2 12 2zm0 5a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm6.5-.25a1.25 1.25 0 1 0-2.5 0 1.25 1.25 0 0 0 2.5 0zM12 9a3 3 0 1 1 0 6 3 3 0 0 1 0-6z"/>
    </svg>`,
  },
  {
    id: "facebook",
    name: "Facebook",
    icon: `<svg class="w-8 h-8" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/>
    </svg>`,
  },
  {
    id: "linkedin",
    name: "LinkedIn",
    icon: `<svg class="w-8 h-8" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
    </svg>`,
  },
  {
    id: "twitter",
    name: "X (Twitter)",
    icon: `<svg class="w-8 h-8" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>`,
  },
];

const instagramFormats = [
  { id: "square", name: "Carré", dimensions: "1080 x 1080 pixels" },
  { id: "landscape", name: "Paysage", dimensions: "1080 × 566 pixels" },
  { id: "portrait", name: "Portrait", dimensions: "1080 × 1350 pixels" },
];

const facebookFormats = [
  { id: "square", name: "Carré", dimensions: "1200 x 1200 pixels" },
  { id: "landscape", name: "Paysage", dimensions: "1200 x 630 pixels" },
];

const twitterFormats = [
  { id: "square", name: "Carré", dimensions: "1080 x 1080 pixels" },
  { id: "landscape", name: "Paysage", dimensions: "1080 x 628 pixels" },
];

const linkedinFormats = [
  { id: "square", name: "Carré", dimensions: "1080 x 1080 pixels" },
  { id: "landscape", name: "Paysage", dimensions: "1080 x 540 pixels" },
];

const selectedNetworkData = computed(() => {
  return networks.find((n: Network) => n.id === selectedNetwork.value);
});

const getFormatName = (formatId: string) => {
  const allFormats = [
    ...instagramFormats,
    ...facebookFormats,
    ...twitterFormats,
    ...linkedinFormats,
  ];
  return allFormats.find((f) => f.id === formatId)?.name || "";
};

const selectNetwork = (networkId: string) => {
  selectedNetwork.value = networkId;
  selectedFormat.value = ""; // Réinitialiser le format lors du changement de réseau
  supportStore.updateSupport({
    network: networkId,
  });
};

const selectFormat = (formatId: string) => {
  selectedFormat.value = formatId;
  const format = [
    ...instagramFormats,
    ...facebookFormats,
    ...twitterFormats,
    ...linkedinFormats,
  ].find((f) => f.id === formatId);
  supportStore.updateSupport({
    format: formatId,
    dimensions: format?.dimensions,
  });
  isMenuOpen.value = false; // Fermer le menu après la sélection
};

// Fonction pour initialiser le support
const initializeSupport = async () => {
  supportStore.resetStore();

  if (isNewSupport.value) {
    await supportStore.initSupport("social");
  } else {
    try {
      await supportStore.loadSupport("social", route.params.id as string);
    } catch (error) {
      console.error("Erreur lors du chargement du support:", error);
    }
  }
};

watch(
  () => route.fullPath,
  () => {
    initializeSupport();
  }
);

onMounted(async () => {
  await initializeSupport();

  // Préremplir les sélections si on édite un support existant
  if (supportStore.currentSupport) {
    if (supportStore.currentSupport.network) {
      selectedNetwork.value = supportStore.currentSupport.network;
    }
    if (supportStore.currentSupport.format) {
      selectedFormat.value = supportStore.currentSupport.format;
    }
  }
});

// Synchroniser si le support est chargé asynchrone
watch(
  () => supportStore.currentSupport,
  (support) => {
    if (support) {
      if (support.network) {
        selectedNetwork.value = support.network;
      }
      // On attend que le réseau soit bien sélectionné avant d'appliquer le format
      if (support.format) {
        let formats: any[] = [];
        if (support.network === "instagram") formats = instagramFormats;
        if (support.network === "facebook") formats = facebookFormats;
        if (support.network === "twitter") formats = twitterFormats;
        if (support.network === "linkedin") formats = linkedinFormats;
        const found = formats.find((f) => f.id === support.format);
        if (found) {
          selectedFormat.value = support.format;
        } else {
          selectedFormat.value = ""; // ou le premier format par défaut
        }
      }
    }
  },
  { immediate: true }
);

// Mettre à jour le support quand le réseau ou le format change
watch([selectedNetwork, selectedFormat], ([newNetwork, newFormat]) => {
  supportStore.updateSupport({
    network: newNetwork,
    format: newFormat,
  });
});

// Ajouter la fonction computed pour déterminer quel composant afficher
const getCanvasComponent = computed(() => {
  if (!selectedNetwork.value || !selectedFormat.value) return null;

  const components: Record<string, Record<string, any>> = {
    instagram: {
      square: InstagramSquareCanva,
      portrait: InstagramPortraitCanva,
      landscape: InstagramLandscapeCanva,
    },
    facebook: {
      square: FacebookSquareCanva,
      landscape: FacebookLandscapeCanva,
    },
    twitter: {
      square: TwitterSquareCanva,
      landscape: TwitterLandscapeCanva,
    },
    linkedin: {
      square: LinkedInSquareCanva,
      landscape: LinkedInLandscapeCanva,
    },
  };

  return components[selectedNetwork.value]?.[selectedFormat.value] || null;
});

// Fonction factorisée pour générer le PDF à partir du composant affiché
const generateAndGetPdfBlob = async (): Promise<Blob> => {
  let previewWasActivated = false;
  if (!supportStore.isPreview) {
    supportStore.togglePreview();
    previewWasActivated = true;
    await new Promise((resolve) => setTimeout(resolve, 500));
  }
  const pdfBlob = await supportCanvasRef.value.generatePdfBlob();
  if (previewWasActivated) {
    supportStore.togglePreview();
  }
  return pdfBlob;
};

const handleSaveAndGeneratePDF = async () => {
  try {
    isSaving.value = true;

    // 1. Récupérer l'état actuel du composant canvas
    let currentState = null;
    if (
      supportCanvasRef.value &&
      typeof supportCanvasRef.value.getCurrentState === "function"
    ) {
      currentState = supportCanvasRef.value.getCurrentState();
      if (currentState) {
        // Met à jour le store avec l'état courant du composant
        supportStore.updateSupport(currentState);
      }
    }

    // 2. Sauvegarder le support d'abord (le PDF est généré côté serveur).
    const response = await supportStore.saveSupport(currentState);
    if (response?.data?.id) {
      supportStore.currentSupport.id = response.data.id;
    }

    // 3. FormData (sans PDF : le serveur génère le vectoriel ; capture en fallback)
    const formData = new FormData();
    formData.append("supportId", supportStore.currentSupport.id.toString());
    formData.append("brandSlug", localStorage.getItem("brand") || "hv");
    formData.append(
      "supportType",
      supportStore.currentSupport.type || supportStore.supportType
    );

    // 4. Persistance du PDF (vectoriel async + notification ; capture en fallback)
    await supportStore.persistSupportPdf(
      formData,
      () => generateAndGetPdfBlob(),
      // Social : capture client (screenshot) — meilleur rendu que le vectoriel.
      undefined,
      true
    );

    playSound(successSound);
    toast.success(response.data.message || "Support sauvegardé avec succès");
    router.push("/documents");
  } catch (error) {
    console.error("Erreur lors de la sauvegarde et génération du PDF:", error);
    toast.error("Une erreur est survenue lors de la sauvegarde du support");
    // Ne pas re-throw l'erreur, juste la logger
  } finally {
    isSaving.value = false;
  }
};
</script>

<script lang="ts">
export default {
  name: "ReseauxView",
};
</script>

<style scoped>
/* Styles pour l'impression et l'aperçu */
@media print {
  .support-preview {
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
  }
}

/* Styles pour l'overlay de chargement */
.pdf-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.pdf-loading-content {
  text-align: center;
}

.loader-standard {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.spinner {
  width: 56px;
  height: 56px;
  border: 6px solid #e5e7eb;
  border-top: 6px solid #b70018;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1.5rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 1.25rem;
  color: #2d3a3a;
  font-weight: 600;
  letter-spacing: 0.02em;
}
</style>
